/**
 * CheckoutInterruptDialog — Ted 09-29 (lane LC). Checkout stays QUIET.
 *
 * Ted: "checkout staying relatively quiet except for credit balances or
 * balances due." So this pops for exactly three registry keys — `credit`,
 * `visit_unpaid`, `balance` (`CHECKOUT_INTERRUPT_KEYS`) — and nothing else:
 * a transport-home or an isolated stay stays row decoration at checkout.
 * Each money fact is acknowledged, then the surface continues into the
 * EXISTING checkout (settle now / settle later / tier confirm) unchanged —
 * this dialog moves no money and makes no decision about it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DogFlagChips } from '../dog-first/DogFlagChips.js';
import { joinNames, type CheckoutInterruptPlan } from './check-in-interrupt.js';
import './CheckInInterrupt.css';

export interface CheckoutInterruptRequest {
  key: string;
  plan: CheckoutInterruptPlan;
  onProceed: () => void | Promise<void>;
  onCancel?: () => void;
}

interface CheckoutInterruptDialogProps {
  request: CheckoutInterruptRequest;
  onProceed: () => void;
  onCancel: () => void;
}

export function CheckoutInterruptDialog({ request, onProceed, onCancel }: CheckoutInterruptDialogProps): JSX.Element {
  const { plan } = request;
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [acked, setAcked] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);
  const complete = useMemo(() => plan.items.every((i) => acked.has(i.id)), [plan, acked]);
  const dogs = joinNames(plan.rows.flatMap((r) => [...r.dogNames]));
  const owner = plan.rows[0]?.customerName ?? '';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkout-interrupt-title"
      data-testid="checkout-interrupt-dialog"
      data-interrupt-key={request.key}
      className="ferro-blocking-dialog__overlay"
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        onCancel();
      }}
    >
      <div className="ferro-blocking-dialog__panel ferro-checkin-interrupt">
        <p className="ferro-checkin-interrupt__eyebrow ferro-checkin-interrupt__eyebrow--quiet">Before you check out</p>
        <h2 id="checkout-interrupt-title">
          {dogs}
          {owner ? ` — ${owner}` : ''}
        </h2>
        <p className="ferro-checkin-interrupt__lead">Money on this account — tick each one, then continue to the checkout.</p>
        <ul className="ferro-checkin-interrupt__list" aria-label="Acknowledge before checkout">
          {plan.items.map((item) => {
            const inputId = `checkout-ack-${item.id}`;
            const on = acked.has(item.id);
            return (
              <li key={item.id}>
                <label
                  htmlFor={inputId}
                  className={`ferro-checkin-interrupt__item${on ? ' ferro-checkin-interrupt__item--acked' : ''}`}
                  data-testid={`checkout-interrupt-item-${item.flag.key}`}
                >
                  <input
                    type="checkbox"
                    id={inputId}
                    name={inputId}
                    checked={on}
                    onChange={(e) =>
                      setAcked((curr) => {
                        const next = new Set(curr);
                        if (e.target.checked) next.add(item.id);
                        else next.delete(item.id);
                        return next;
                      })
                    }
                    data-testid={`checkout-interrupt-ack-${item.flag.key}`}
                  />
                  <span className="ferro-checkin-interrupt__item-body">
                    <DogFlagChips flags={[item.flag]} scope="account" testIdPrefix="checkout-interrupt-" />
                    <span>{item.flag.detail}</span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
        <div className="ferro-blocking-dialog__actions">
          <button type="button" className="ferro-btn ferro-btn--ghost" ref={cancelRef} onClick={onCancel} data-testid="checkout-interrupt-cancel">
            Not now
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            disabled={!complete}
            onClick={onProceed}
            data-testid="checkout-interrupt-proceed"
          >
            Continue to check out
          </button>
        </div>
      </div>
    </div>
  );
}

export interface CheckoutInterrupt {
  ask: (request: CheckoutInterruptRequest) => void;
  pending: boolean;
  dialog: JSX.Element | null;
}

/** The queue behind the quiet checkout step — one request at a time. */
export function useCheckoutInterrupt(): CheckoutInterrupt {
  const [queue, setQueue] = useState<CheckoutInterruptRequest[]>([]);
  const ask = useCallback((request: CheckoutInterruptRequest) => {
    setQueue((curr) => (curr.some((r) => r.key === request.key) ? curr : [...curr, request]));
  }, []);
  const shift = useCallback(() => setQueue((curr) => curr.slice(1)), []);
  const head = queue[0] ?? null;
  const dialog = head ? (
    <CheckoutInterruptDialog
      key={head.key}
      request={head}
      onProceed={() => {
        shift();
        void head.onProceed();
      }}
      onCancel={() => {
        shift();
        head.onCancel?.();
      }}
    />
  ) : null;
  return { ask, pending: head !== null, dialog };
}
